'use client'

import { useEffect, useState } from 'react'
import { useLocale } from 'next-intl'
import { usePathname, useRouter } from '@/i18n/navigation'
import { isLocale, type Locale } from '@/i18n/config'
import type { SuggestionMessages } from '@/i18n/suggestion'
import { fetchActiveLocales } from '@/lib/active-locales'
import {
  localeCookieString,
  matchBrowserLocale,
  readLocaleCookie,
} from '@/lib/locale-preference'
import { getMe } from '@/lib/auth-store'

function persistLocale(locale: Locale) {
  document.cookie = localeCookieString(locale, window.location.protocol === 'https:')
}

/**
 * Dismissible strip suggesting the visitor's browser language, shown to
 * anonymous visitors whose `navigator.languages` best match differs from the
 * page locale. Text renders in the SUGGESTED language (its reader's own).
 * Any interaction persists a `pf_locale` cookie; the proxy then routes
 * return visits from bare paths (see src/proxy.ts). Never IP-based.
 */
export function LocaleSuggestionBanner({
  messages,
}: {
  messages: Record<Locale, SuggestionMessages>
}) {
  const rawLocale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const [suggestion, setSuggestion] = useState<Locale | null>(null)

  useEffect(() => {
    // All inputs are client-only (cookie, auth store, navigator); deciding in
    // an effect also keeps server and first client render identical (null).
    if (getMe() || readLocaleCookie(document.cookie)) return
    let mounted = true
    fetchActiveLocales().then((active) => {
      if (!mounted) return
      const match = matchBrowserLocale(navigator.languages, active)
      if (match && match !== rawLocale) setSuggestion(match)
    })
    return () => {
      mounted = false
    }
  }, [rawLocale])

  if (!suggestion) return null
  const t = messages[suggestion]
  const pageLocale: Locale = isLocale(rawLocale) ? rawLocale : 'en'

  function view() {
    if (!suggestion) return
    persistLocale(suggestion)
    setSuggestion(null)
    // Read the query at click time; useSearchParams would force a Suspense
    // boundary into every static page that renders the banner.
    router.replace(`${pathname}${window.location.search}`, { locale: suggestion })
  }

  function dismiss() {
    persistLocale(pageLocale)
    setSuggestion(null)
  }

  return (
    <div
      lang={suggestion}
      className="flex items-center justify-center gap-3 border-b border-white/10 bg-elevated/95 px-4 py-2 text-sm text-white/90"
    >
      <span>{t.message}</span>
      <button
        type="button"
        onClick={view}
        className="rounded-full bg-flamingo/15 px-3 py-1 text-xs font-semibold text-flamingo transition-colors hover:bg-flamingo/25"
      >
        {t.view}
      </button>
      <button
        type="button"
        aria-label={t.dismiss}
        onClick={dismiss}
        className="text-white/50 transition-colors hover:text-white"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
          className="h-4 w-4"
        >
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  )
}
